import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaBookOpen,
  FaCheck,
  FaChalkboardTeacher,
  FaSave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/payroll.css";

const SUBJECT_TYPES = [
  { value: "Main", label: "Main" },
  { value: "Optional", label: "Optional" },
];

//Created By Cursor
export default function MdlStaffSubject({
  open,
  editing,
  staffName,
  subjects = [],
  classes = [],
  initial,
  saving,
  onClose,
  onSave,
}) {
  const [subjectId, setSubjectId] = useState("");
  const [subjectType, setSubjectType] = useState("Main");
  const [classIds, setClassIds] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubjectId(initial?.subject_id || "");
    setSubjectType(initial?.subject_type || "Main");
    setClassIds(
      Array.isArray(initial?.teaching_classes)
        ? initial.teaching_classes.map(String)
        : []
    );
    setQuery("");
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && typeof onClose === "function") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const filteredSubjects = useMemo(() => {
    const list = Array.isArray(subjects) ? subjects : [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      String(s.subject_name || s.subject_id || "")
        .toLowerCase()
        .includes(q)
    );
  }, [subjects, query]);

  const selectedSubject = useMemo(
    () =>
      (subjects || []).find((s) => String(s.subject_id) === String(subjectId)),
    [subjects, subjectId]
  );

  const toggleClass = (id) => {
    const key = String(id);
    setClassIds((prev) => {
      const next = new Set((prev || []).map(String));
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return Array.from(next);
    });
  };

  const handleSave = () => {
    if (typeof onSave !== "function") return;
    onSave({
      subject_id: subjectId,
      subject_type: subjectType,
      teaching_classes: classIds,
    });
  };

  if (!open) return null;

  const modal = (
    <div
      className="ss-assign-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ss-assign-mdl-title"
    >
      <button
        type="button"
        className="ss-assign-mdl__backdrop"
        aria-label="Close"
        disabled={saving}
        onClick={onClose}
      />
      <div className="ss-assign-mdl__panel">
        <header
          className="ss-assign-mdl__hero"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`,
          }}
        >
          <div className="ss-assign-mdl__hero-main">
            <div className="ss-assign-mdl__hero-icon" aria-hidden>
              <FaChalkboardTeacher />
            </div>
            <div className="min-w-0">
              <p className="ss-assign-mdl__eyebrow">
                {editing ? "Edit mapping" : "Add mapping"}
              </p>
              <h2 id="ss-assign-mdl-title" className="ss-assign-mdl__title">
                {staffName || "Staff subject"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="ss-assign-mdl__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="ss-assign-mdl__body">
          <section className="ss-assign-mdl__section">
            <div className="ss-assign-mdl__section-head">
              <h3>Type</h3>
            </div>
            <div className="ss-assign-mdl__types" role="radiogroup">
              {SUBJECT_TYPES.map((t) => {
                const active = subjectType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`ss-assign-type${active ? " is-active" : ""}`}
                    onClick={() => setSubjectType(t.value)}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="ss-assign-mdl__section">
            <div className="ss-assign-mdl__section-head">
              <h3>Subject</h3>
              <span>{filteredSubjects.length} available</span>
            </div>
            {!editing ? (
              <label className="ss-assign-mdl__search">
                <FaSearch aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search subjects…"
                  aria-label="Search subjects"
                />
              </label>
            ) : null}
            {editing ? (
              <div className="ss-assign-mdl__locked">
                <FaBookOpen aria-hidden />
                <strong>
                  {selectedSubject?.subject_name || subjectId || "Subject"}
                </strong>
              </div>
            ) : filteredSubjects.length === 0 ? (
              <p className="ss-assign-mdl__empty">No subjects found.</p>
            ) : (
              <div className="ss-assign-mdl__options" role="listbox">
                {filteredSubjects.map((s) => {
                  const active = String(s.subject_id) === String(subjectId);
                  return (
                    <button
                      key={s.subject_id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`ss-assign-opt${active ? " is-active" : ""}`}
                      onClick={() => setSubjectId(s.subject_id)}
                    >
                      <span className="ss-assign-opt__icon" aria-hidden>
                        <FaBookOpen />
                      </span>
                      <span className="ss-assign-opt__text">
                        <strong>{s.subject_name || s.subject_id}</strong>
                      </span>
                      {active ? (
                        <span className="ss-assign-opt__check" aria-hidden>
                          <FaCheck />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="ss-assign-mdl__section">
            <div className="ss-assign-mdl__section-head">
              <h3>Teaching classes</h3>
              <span>{classIds.length} selected</span>
            </div>
            {!classes.length ? (
              <p className="ss-assign-mdl__empty">
                No classes found for the current session.
              </p>
            ) : (
              <div className="ss-assign-mdl__classes">
                {classes.map((c) => {
                  const active = classIds.map(String).includes(String(c.class_id));
                  return (
                    <button
                      key={c.class_id}
                      type="button"
                      className={`ss-assign-class${active ? " is-active" : ""}`}
                      onClick={() => toggleClass(c.class_id)}
                    >
                      {active ? <FaCheck aria-hidden /> : null}
                      <span>{c.class_name || c.class_id}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="ss-assign-mdl__footer">
          <button
            type="button"
            className="ss-assign-mdl__btn ss-assign-mdl__btn--ghost"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ss-assign-mdl__btn ss-assign-mdl__btn--primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FaSave />
            <span>{saving ? "Saving…" : editing ? "Update" : "Add"}</span>
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
